import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import { darken, useTheme } from "@mui/material/styles";
import { ResponsiveBar } from "@nivo/bar";
import MainContext from "../../../Context/MainContext/MainContext";
import ReactDOM from "react-dom";

import MUIBox from "@mui/material/Box";
import MUICheckIcon from "@mui/icons-material/Check";

import Widget from "../../Widget";

import Region from "../../../Utility/Crud/Region";
import Location from "../../../Utility/Crud/Location";
import Format from "../../../Utility/Format";

/**
 * Computes the aggregate tier point thresholds across every location that is
 * present in the widget payload. For each location, looks up the
 * `jsonTierPoints` entry whose `begin`/`end` date range contains the current
 * date and sums `tier1Points`, `tier2Points`, and `tier3Points` across those
 * matching entries.
 *
 * @param {object|null} widgetData Loaded widget response data.
 * @param {object} locationsIndexed Locations indexed by identifier, from `MainContext.locationsIndexed`.
 * @returns {{ tier1Points: number, tier2Points: number, tier3Points: number }}
 */
function getTierPoints(widgetData, locationsIndexed) {
  const currentDate = new Date();

  let tier1Points = 0;
  let tier2Points = 0;
  let tier3Points = 0;

  if (
    widgetData === null ||
    widgetData === undefined ||
    widgetData.locations === null ||
    widgetData.locations === undefined ||
    locationsIndexed === null ||
    locationsIndexed === undefined
  ) {
    return {
      tier1Points: tier1Points,
      tier2Points: tier2Points,
      tier3Points: tier3Points,
    };
  }

  Object.keys(widgetData.locations).forEach((locationId) => {
    const locationFromContext = locationsIndexed[locationId];

    if (
      locationFromContext === null ||
      locationFromContext === undefined ||
      locationFromContext.jsonTierPoints === null ||
      locationFromContext.jsonTierPoints === undefined
    ) {
      return;
    }

    let tierPointsArray;
    try {
      tierPointsArray = JSON.parse(locationFromContext.jsonTierPoints);
    } catch (parseError) {
      console.error(
        `Failed to parse jsonTierPoints for location ${locationId}:`,
        parseError,
      );
      return;
    }

    const matchingTierPointsSection = tierPointsArray.find(
      (tierPointsSection) => {
        const beginDate = new Date(tierPointsSection.begin);
        const endDate = new Date(tierPointsSection.end);
        return currentDate >= beginDate && currentDate <= endDate;
      },
    );

    if (matchingTierPointsSection === undefined) {
      return;
    }

    tier1Points += matchingTierPointsSection.tier1Points || 0;
    tier2Points += matchingTierPointsSection.tier2Points || 0;
    tier3Points += matchingTierPointsSection.tier3Points || 0;
  });

  return {
    tier1Points: tier1Points,
    tier2Points: tier2Points,
    tier3Points: tier3Points,
  };
}

/**
 * Builds the points widget subtitle from `meta.mode` and related identifiers
 * (`regionIds`, `locationIds`). Returns null when there is no scope label to show.
 *
 * @param {object|null|undefined} widgetData Loaded widget response data.
 * @param {object} locationsIndexed Locations indexed by identifier from `MainContext.locationsIndexed`.
 * @param {object} regionsIndexed Regions indexed by identifier from `MainContext.regionsIndexed`.
 * @returns {string|null} Caption text such as `Global`, a region or location name, a multiple-selection label, or null.
 */
function buildPointsWidgetSubtitleText(
  widgetData,
  locationsIndexed,
  regionsIndexed,
) {
  if (widgetData?.meta === null || widgetData?.meta === undefined) {
    return null;
  }

  if (widgetData?.meta.mode === "global") {
    return "Global";
  }

  if (widgetData?.meta.mode === "region") {
    if (widgetData?.meta.regionIds.length === 1) {
      return Region.getDisplayName(
        regionsIndexed?.[widgetData?.meta.regionIds[0]],
      );
    }
    return "Multiple Regions";
  }

  if (widgetData?.meta.mode === "location") {
    if (widgetData?.meta.locationIds.length === 1) {
      return Location.getDisplayName(
        locationsIndexed?.[widgetData?.meta.locationIds[0]],
      );
    }
    return "Multiple Locations";
  }

  return null;
}

/**
 * Builds the points display values from the widget response payload.
 *
 * @param {object|null} widgetData Loaded widget response data.
 * @param {object} locationsIndexed Locations indexed by identifier, from `MainContext.locationsIndexed`.
 * @returns {object}
 */
function getPointsDisplayValues(widgetData, locationsIndexed) {
  const { tier1Points, tier2Points, tier3Points } = getTierPoints(
    widgetData,
    locationsIndexed,
  );

  const deliveredPointsRaw = Object.values(widgetData?.locations || {}).reduce(
    (accumulator, location) => {
      const locationDeliveredPoints = Number(location?.deliveredPoints);
      const safeAddend = Number.isNaN(locationDeliveredPoints)
        ? 0
        : locationDeliveredPoints;
      return accumulator + safeAddend;
    },
    0,
  );
  const scheduledPointsRaw = Object.values(widgetData?.locations || {}).reduce(
    (accumulator, location) => {
      const locationScheduledPoints = Number(location?.scheduledPoints);
      const safeAddend = Number.isNaN(locationScheduledPoints)
        ? 0
        : locationScheduledPoints;
      return accumulator + safeAddend;
    },
    0,
  );

  const deliveredPoints = Format.number(deliveredPointsRaw, {
    decimalPlaceCount: 2,
    shouldRoundValueToDecimalPlacesBeforeFormatting: true,
    shouldReturnRoundedNumericValue: true,
  });
  const scheduledPoints = Format.number(scheduledPointsRaw, {
    decimalPlaceCount: 2,
    shouldRoundValueToDecimalPlacesBeforeFormatting: true,
    shouldReturnRoundedNumericValue: true,
  });

  const totalPointsInProgress = deliveredPoints + scheduledPoints;
  const pointsRemainingToMaximumTier = Math.max(
    tier3Points - totalPointsInProgress,
    0,
  );

  return {
    deliveredPoints: deliveredPoints,
    scheduledPoints: scheduledPoints,
    pointsRemainingToMaximumTier: pointsRemainingToMaximumTier,
    tier1Points: tier1Points,
    tier2Points: tier2Points,
    tier3Points: tier3Points,
  };
}

/**
 * Renders a horizontal stacked bar that represents progress toward the
 * maximum commission tier using Nivo. The bar is composed of
 * three segments (delivered, scheduled, remaining) and is overlaid with
 * vertical tier marker lines drawn with chart markers.
 *
 * The chart x-axis scale and the tier markers drawn on the bar adapt to the
 * user's current tier progress phase:
 *   - Below tier 1: only the T1 marker is drawn; the scale runs from 0 to
 *     T1 + 20%.
 *   - At or above tier 1 but below tier 3: T1, T2, and T3 markers are drawn;
 *     the scale runs from T1 - 10% to T3 + 20% to make tier spacing easier
 *     to visualize.
 *   - At or above tier 3: only the T3 marker is drawn; the scale runs from
 *     T3 - 10% to max(T3 + 30%, current points + 10%) so the bar continues
 *     to extend as the user accumulates points beyond tier 3.
 *
 * @param {object} props
 * @param {number} props.deliveredPoints Points already delivered.
 * @param {number} props.scheduledPoints Points scheduled but not yet delivered.
 * @param {number} props.tier1Points Aggregate Tier 1 threshold across all locations in the payload.
 * @param {number} props.tier2Points Aggregate Tier 2 threshold across all locations in the payload.
 * @param {number} props.tier3Points Aggregate Tier 3 threshold across all locations in the payload.
 * @returns {React.ReactElement}
 */
function WidgetDashboardPointsChart({
  deliveredPoints,
  scheduledPoints,
  tier1Points,
  tier2Points,
  tier3Points,
}) {
  const materialUiTheme = useTheme();
  const chartContainerElementReference = useRef(null);
  const [pointsChartTooltipState, setPointsChartTooltipState] = useState({
    isVisible: false,
    anchorLeftPixels: 0,
    anchorTopPixels: 0,
  });

  const tierMarkerDefinitions = useMemo(() => {
    return [
      { label: "T1", points: tier1Points },
      { label: "T2", points: tier2Points },
      { label: "T3", points: tier3Points },
    ];
  }, [tier1Points, tier2Points, tier3Points]);

  const totalCurrentPoints = deliveredPoints + scheduledPoints;

  /**
   * Computes the chart scale (minimum/maximum point values used as the x-axis bounds)
   * and which tier markers are drawn on the bar based on the current tier progress phase:
   *   - Phase "below tier 1": only the T1 marker is shown so the user focuses on the
   *     next achievable tier. The scale runs from 0 to T1 + 20%.
   *   - Phase "between tier 1 and tier 3": all three markers are shown so the user can
   *     visualize their position between achieved and remaining tiers. The scale is
   *     zoomed to T1 - 10% through T3 + 20% to make tier spacing easier to read.
   *   - Phase "at or above tier 3": only the T3 marker is shown to celebrate the
   *     achievement while still encouraging further progress. The scale runs from
   *     T3 - 10% through max(T3 + 30%, current points + 10%) so the bar continues to
   *     extend as the user accumulates additional points beyond tier 3.
   */
  const {
    chartScaleMinimumPoints,
    chartScaleMaximumPoints,
    visibleTierMarkerDefinitions,
  } = useMemo(() => {
    if (tier1Points <= 0) {
      return {
        chartScaleMinimumPoints: 0,
        chartScaleMaximumPoints: Math.max(tier3Points, totalCurrentPoints, 1),
        visibleTierMarkerDefinitions: [],
      };
    }

    if (totalCurrentPoints < tier1Points) {
      return {
        chartScaleMinimumPoints: 0,
        chartScaleMaximumPoints: tier1Points * 1.2,
        visibleTierMarkerDefinitions: [{ label: "T1", points: tier1Points }],
      };
    }

    if (totalCurrentPoints < tier3Points) {
      return {
        chartScaleMinimumPoints: tier1Points * 0.9,
        chartScaleMaximumPoints: tier3Points * 1.2,
        visibleTierMarkerDefinitions: [
          { label: "T1", points: tier1Points },
          { label: "T2", points: tier2Points },
          { label: "T3", points: tier3Points },
        ],
      };
    }

    return {
      chartScaleMinimumPoints: tier3Points * 0.9,
      chartScaleMaximumPoints: Math.max(
        tier3Points * 1.3,
        totalCurrentPoints * 1.1,
      ),
      visibleTierMarkerDefinitions: [{ label: "T3", points: tier3Points }],
    };
  }, [tier1Points, tier2Points, tier3Points, totalCurrentPoints]);

  const remainingPointsToFillChartScale = Math.max(
    chartScaleMaximumPoints - totalCurrentPoints,
    0,
  );

  const deliveredBarBackgroundColor = materialUiTheme.palette.primary.main;
  const scheduledBarBackgroundColor = materialUiTheme.palette.secondary.main;
  const remainingBarBackgroundColor =
    materialUiTheme.palette.background.default;
  const chartBarColors =
    pointsChartTooltipState.isVisible === true
      ? [
          darken(deliveredBarBackgroundColor, 0.2),
          darken(scheduledBarBackgroundColor, 0.2),
          darken(remainingBarBackgroundColor, 0.2),
        ]
      : [
          deliveredBarBackgroundColor,
          scheduledBarBackgroundColor,
          remainingBarBackgroundColor,
        ];

  /**
   * Builds the data for the points bar chart.
   */
  const pointsBarChartData = useMemo(() => {
    return [
      {
        points: "Points",
        delivered: deliveredPoints,
        scheduled: scheduledPoints,
        remaining: remainingPointsToFillChartScale,
      },
    ];
  }, [deliveredPoints, scheduledPoints, remainingPointsToFillChartScale]);

  /**
   * Shows the points tooltip below the chart when the pointer enters the bar.
   *
   * @returns {void}
   */
  const handleChartContainerPointerEnter = () => {
    const chartContainerElement = chartContainerElementReference.current;
    if (chartContainerElement === null || chartContainerElement === undefined) {
      return;
    }

    const chartContainerBoundingRectangle =
      chartContainerElement.getBoundingClientRect();
    setPointsChartTooltipState({
      isVisible: true,
      anchorLeftPixels:
        chartContainerBoundingRectangle.left +
        chartContainerBoundingRectangle.width / 2,
      anchorTopPixels: chartContainerBoundingRectangle.bottom,
    });
  };

  const handleChartContainerPointerLeave = () => {
    setPointsChartTooltipState((previousTooltipState) => {
      if (previousTooltipState.isVisible === false) {
        return previousTooltipState;
      }
      return { ...previousTooltipState, isVisible: false };
    });
  };

  /**
   * Renders tier markers over only the stacked bar and centers each label beneath its line.
   *
   * @param {object} chartLayerProperties Nivo bar layer properties.
   * @returns {React.ReactElement|null} Tier marker lines and labels, or null before bars are available.
   */
  const renderTierMarkers = (chartLayerProperties) => {
    const firstBar = chartLayerProperties.bars[0];
    if (firstBar === undefined) {
      return null;
    }

    const markerTopPosition = firstBar.y;
    const markerBottomPosition = firstBar.y + firstBar.height;

    return (
      <g pointerEvents="none">
        {visibleTierMarkerDefinitions
          .filter((tierMarker) => tierMarker.points > 0)
          .map((tierMarker) => {
            const markerHorizontalPosition = chartLayerProperties.xScale(
              tierMarker.points,
            );

            return (
              <g key={tierMarker.label}>
                <line
                  stroke={materialUiTheme.palette.text.primary}
                  strokeWidth={2}
                  x1={markerHorizontalPosition}
                  x2={markerHorizontalPosition}
                  y1={markerTopPosition}
                  y2={markerBottomPosition}
                />
                <text
                  fill={materialUiTheme.palette.text.primary}
                  fontSize={10}
                  textAnchor="middle"
                  x={markerHorizontalPosition}
                  y={markerBottomPosition + 10}
                >
                  {tierMarker.label}
                </text>
              </g>
            );
          })}
      </g>
    );
  };

  return (
    <>
      <MUIBox
        ref={chartContainerElementReference}
        onPointerEnter={handleChartContainerPointerEnter}
        onPointerLeave={handleChartContainerPointerLeave}
        sx={{
          width: "100%",
          height: 42,
          minHeight: 42,
          flexShrink: 0,
          position: "relative",
        }}
      >
        <ResponsiveBar
          data={pointsBarChartData}
          keys={["delivered", "scheduled", "remaining"]}
          indexBy="points"
          layout="horizontal"
          margin={{ top: 0, right: 0, bottom: 12, left: 0 }}
          minValue={chartScaleMinimumPoints}
          maxValue={chartScaleMaximumPoints}
          padding={0.4}
          groupMode="stacked"
          colors={chartBarColors}
          axisTop={null}
          axisRight={null}
          axisBottom={null}
          axisLeft={null}
          enableGridX={false}
          enableGridY={false}
          enableLabel={false}
          isInteractive={false}
          layers={["bars", renderTierMarkers]}
        />
      </MUIBox>

      {/* Tooltip for the points chart. */}
      {pointsChartTooltipState.isVisible === true &&
        ReactDOM.createPortal(
          <MUIBox
            sx={{
              position: "fixed",
              left: `${pointsChartTooltipState.anchorLeftPixels}px`,
              top: `${pointsChartTooltipState.anchorTopPixels}px`,
              transform: "translate(-50%, 8px)",
              pointerEvents: "none",
              backgroundColor: (theme) => theme.palette.background.paper,
              color: (theme) => theme.palette.text.primary,
              padding: "8px 10px",
              borderRadius: 1,
              fontSize: "0.9rem",
              lineHeight: 1.3,
              whiteSpace: "nowrap",
              zIndex: (theme) => theme.zIndex.tooltip,
              boxShadow: 4,
              minWidth: 220,
            }}
          >
            {/* Tier markers: label, gap or achieved check, threshold points */}
            <MUIBox
              sx={{ display: "flex", flexDirection: "column", gap: "4px" }}
            >
              {tierMarkerDefinitions.map((tierMarker) => {
                const tierThresholdPoints = tierMarker.points;
                const hasHitTier =
                  tierThresholdPoints > 0 &&
                  totalCurrentPoints >= tierThresholdPoints;
                const pointsDifferenceFromTierThreshold =
                  totalCurrentPoints - tierThresholdPoints;

                return (
                  <MUIBox
                    key={tierMarker.label}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "auto minmax(0, 1fr)",
                      alignItems: "center",
                      columnGap: "12px",
                      width: "100%",
                    }}
                  >
                    <MUIBox component="span">{tierMarker.label}</MUIBox>
                    <MUIBox
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        columnGap: "10px",
                        minWidth: 0,
                      }}
                    >
                      {tierThresholdPoints <= 0 ? null : hasHitTier === true ? (
                        <MUICheckIcon
                          sx={{
                            color: "success.main",
                            fontSize: "1.15rem",
                            flexShrink: 0,
                          }}
                          aria-hidden
                        />
                      ) : (
                        <MUIBox
                          component="span"
                          sx={{
                            color: "error.main",
                            fontVariantNumeric: "tabular-nums",
                            flexShrink: 0,
                            textAlign: "right",
                          }}
                        >
                          {Format.number(pointsDifferenceFromTierThreshold, {
                            decimalPlaceCount: 2,
                            shouldRoundValueToDecimalPlacesBeforeFormatting: true,
                          })}{" "}
                        </MUIBox>
                      )}
                      <MUIBox
                        component="span"
                        sx={{
                          fontVariantNumeric: "tabular-nums",
                          textAlign: "right",
                          flexShrink: 0,
                        }}
                      >
                        {Format.number(tierMarker.points)}{" "}
                        {tierMarker.points === 1 ? "point" : "points"}
                      </MUIBox>
                    </MUIBox>
                  </MUIBox>
                );
              })}
            </MUIBox>

            {/* Divider */}
            <MUIBox
              sx={{
                height: "1px",
                backgroundColor: (theme) => theme.palette.divider,
                marginTop: "6px",
                marginBottom: "6px",
              }}
            />

            {/* Tooltip Progress rows */}
            <MUIBox
              sx={{ display: "flex", flexDirection: "column", gap: "2px" }}
            >
              {[
                {
                  key: "delivered",
                  label: "Delivered",
                  value: deliveredPoints,
                  color: deliveredBarBackgroundColor,
                },
                {
                  key: "scheduled",
                  label: "Scheduled",
                  value: scheduledPoints,
                  color: scheduledBarBackgroundColor,
                },
              ].map((tooltipProgressRow) => (
                <MUIBox
                  key={tooltipProgressRow.key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 3,
                  }}
                >
                  <MUIBox
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <MUIBox
                      sx={{
                        width: 10,
                        height: 10,
                        backgroundColor: tooltipProgressRow.color,
                        flexShrink: 0,
                      }}
                    />
                    <MUIBox component="span">{tooltipProgressRow.label}</MUIBox>
                  </MUIBox>
                  <MUIBox
                    component="span"
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {Format.number(tooltipProgressRow.value, {
                      decimalPlaceCount: 2,
                      shouldRoundValueToDecimalPlacesBeforeFormatting: true,
                    })}{" "}
                    {tooltipProgressRow.value === 1 ? "point" : "points"}
                  </MUIBox>
                </MUIBox>
              ))}
            </MUIBox>
          </MUIBox>,
          document.body,
        )}
    </>
  );
}

/**
 * Renders the points progress chart for loaded widget data.
 *
 * @param {object} props
 * @param {object|null} props.widgetData Loaded widget response data.
 * @returns {React.ReactElement}
 */
function WidgetDashboardPointsContent({ widgetData }) {
  const mainContext = useContext(MainContext);

  const {
    deliveredPoints,
    scheduledPoints,
    tier1Points,
    tier2Points,
    tier3Points,
  } = getPointsDisplayValues(widgetData, mainContext.locationsIndexed);

  return (
    <WidgetDashboardPointsChart
      deliveredPoints={deliveredPoints}
      scheduledPoints={scheduledPoints}
      tier1Points={tier1Points}
      tier2Points={tier2Points}
      tier3Points={tier3Points}
    />
  );
}

/**
 * Sales queue dashboard widget: delivered and scheduled points toward commission tiers.
 *
 * @param {object} props
 * @param {boolean} props.isActive Whether the widget is currently selected.
 * @param {object} props.dashboardWidget Dashboard widget configuration object.
 * @param {object} props.widgetId Widget identifier.
 * @returns {React.ReactElement}
 */
export default function WidgetDashboardPoints(props) {
  const mainContext = useContext(MainContext);

  return (
    <Widget
      getWidgetId={() => props.widgetId}
      getTitle={() => props.dashboardWidget.name}
      isActive={() => props.isActive}
      getSubtitle={(widgetRenderProperties) => {
        const widgetData = widgetRenderProperties.widgetData;
        if (widgetData === null || widgetData === undefined) {
          return null;
        }
        return buildPointsWidgetSubtitleText(
          widgetData,
          mainContext.locationsIndexed,
          mainContext.regionsIndexed,
        );
      }}
      renderContent={({ widgetData }) => (
        <WidgetDashboardPointsContent widgetData={widgetData} />
      )}
    />
  );
}
